import {
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Flex, Tag, Tooltip } from "antd";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { useNotify } from "../../../components/Notification";
import { getMenuOfUser } from "../../../redux/menu/getMenuOfUser";
import { menuAPIWithErrorHandling } from "../../../api/menus/menu";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";

const RowContext = createContext({});

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["url", "is_active"];

const ListMenu = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  //#region state
  const [treeData, setTreeData] = useState([]);
  const [visibleList, setVisibleList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalTreeData, setOriginalTreeData] = useState([]);
  //#endregion state

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  //#region  function
  const buildVisibleList = (tree, level = 0) => {
    let result = [];
    for (let item of tree) {
      result.push({ ...item, level });
      if (item.children && item.children.length > 0) {
        result = result.concat(buildVisibleList(item.children, level + 1));
      }
    }
    return result;
  };

  const fetchActions = async () => {
    try {
      setIsLoading(true);
      const res = await menuAPIWithErrorHandling.getActions();

      if (res.data.success) {
        const data = res.data.data;
        setTreeData(data);
        setOriginalTreeData(JSON.parse(JSON.stringify(data)));
        setVisibleList(buildVisibleList(data));
      }
    } catch (err) {
      notify.error({
        message: "Thất bại",
        description: err.response?.data?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const moveItemInTree = (tree, visibleList, activeId, overId) => {
    const clone = JSON.parse(JSON.stringify(tree));

    const findAndRemove = (items, id) => {
      for (let i = 0; i < items.length; i++) {
        if (items[i].id === id) return items.splice(i, 1)[0];
        if (items[i].children) {
          const result = findAndRemove(items[i].children, id);
          if (result) return result;
        }
      }
      return null;
    };

    const getParent = (items, id, parent = null) => {
      for (let item of items) {
        if (item.id === id) return parent;
        if (item.children) {
          const found = getParent(item.children, id, item);
          if (found) return found;
        }
      }
      return null;
    };

    const insertAtIndex = (items, item, index) => {
      if (index < 0) {
        items.push(item);
      } else {
        items.splice(index, 0, item);
      }
    };

    const activeItem = findAndRemove(clone, activeId);
    if (!activeItem) return tree;

    const activeIndex = visibleList.findIndex((i) => i.id === activeId);
    const overIndex = visibleList.findIndex((i) => i.id === overId);

    const overParent = getParent(clone, overId);
    let siblings = overParent ? overParent.children : clone;

    const siblingOverIndex = siblings.findIndex((i) => i.id === overId);

    if (activeIndex < overIndex) {
      // Kéo xuống → chèn sau over
      insertAtIndex(siblings, activeItem, siblingOverIndex + 1);
    } else {
      // Kéo lên → chèn trước over
      insertAtIndex(siblings, activeItem, siblingOverIndex);
    }

    return clone;
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const newTree = moveItemInTree(treeData, visibleList, active.id, over.id);
    setTreeData(newTree);

    setVisibleList(buildVisibleList(newTree));
    setHasChanges(true);
  };

  const handleSaveLocation = async () => {
    const buildSaveList = (tree, parentId = null) => {
      return tree.flatMap((item, index) => {
        const newItem = {
          ...item,
          sort_order: index + 1,
          parent_id: parentId,
        };
        return [
          newItem,
          ...(item.children ? buildSaveList(item.children, item.id) : []),
        ];
      });
    };

    const currentList = buildSaveList(treeData);
    const originalList = buildSaveList(originalTreeData);

    const changedItems = currentList.filter((item, index) => {
      const originalItem = originalList.find((o) => o.id === item.id);
      return (
        !originalItem ||
        originalItem.sort_order !== item.sort_order ||
        originalItem.parent_id !== item.parent_id
      );
    });

    try {
      setIsLoading(true);
      const paramDatas = {
        menu_items: changedItems
      };

      const res = await menuAPIWithErrorHandling.sortMenu(paramDatas);

      if (res.data.success) {
        notify.success({
          message: "Thành công",
          description: "Sắp xếp menu thành công",
        });
        // Vừa đổi thứ tự menu -> bỏ qua cửa sổ dedupe của SWR, lấy bản mới ngay.
        dispatch(getMenuOfUser({ force: true }));
        setHasChanges(false);
      }
    } catch (err) {
      notify.error({
        message: "Thất bại",
        description: err?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const showDeleteConfirm = async (record) => {
    const confirmed = await confirmDelete({ entity: "menu", name: record.name });
    if (!confirmed) return;

    try {
      await menuAPIWithErrorHandling.deleteMenu(record.id);

      notify.success({
        message: "Thành công",
        description: "Bạn đã xóa menu thành công!",
      });

      await fetchActions();
    } catch (error) {
      notify.error({
        message: "Thất bại",
        description: error?.message,
      });
    }
  };

  const Row = (props) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: props["data-row-key"] });
    const style = {
      ...props.style,
      transform: CSS.Transform.toString(transform),
      transition,
      ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
    };
    const contextValue = useMemo(
      () => ({ setActivatorNodeRef, listeners }),
      [setActivatorNodeRef, listeners]
    );
    return (
      <RowContext.Provider value={contextValue}>
        <tr {...props} ref={setNodeRef} style={style} {...attributes} />
      </RowContext.Provider>
    );
  };

  const DragHandle = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined />}
        style={{ cursor: "move" }}
        ref={setActivatorNodeRef}
        {...listeners}
      />
    );
  };

  const handleUpdateStatus = async (record) => {
    try {
      setIsLoading(true);
      const paramDatas = {
        is_active: !record.is_active
      };

      const response = await menuAPIWithErrorHandling.statusMenu(paramDatas);

      if (response) {
        notify.success({
          message: "Thành công",
          description: "Cập nhật Trạng thái thành công!",
        });
        fetchActions();
        setIsLoading(false);
      }
    } catch (error) {
      notify.error({
        message: "Thất bại",
        description: error?.message,
      });
    } finally {
    }
  };
  //#endregion function

  //#region table
  const columnDefs = [
    {
      key: "index", label: "#", fixed: true,
      column: {
        title: "#", key: "index", render: () => <DragHandle />, width: 50, fixed: 'left',
      },
    },
    {
      key: "name", label: "Tên Menu", fixed: true,
      column: {
        title: "Tên Menu", key: "name", width: 200, fixed: 'left',
        render: (_, record) => (
          <Flex
            align="center"
            gap={10}
            style={{
              paddingLeft: record.level * 32,
            }}
          >
            {record.level === 0 && <i className={record.icon} />}
            <span
              style={{
                color: record.level === 0 ? "#4096ff" : "normal",
                fontWeight: record.level === 0 ? "bold" : "normal",
              }}
            >
              {record.name}
            </span>
          </Flex>
        ),
      },
    },
    {
      key: "url", label: "Đường dẫn",
      column: {
        title: "Đường dẫn", dataIndex: "url", key: "url", width: 250,
      },
    },
    {
      key: "is_active", label: "Trạng thái",
      column: {
        title: "Trạng thái", key: "is_active", align: "center", width: 150,
        render: (_, record) => (
          <Tooltip title={"Cập nhật trạng thái"}>
            <Tag
              color={record.is_active === 1 ? "green" : "red"}
              onClick={() => handleUpdateStatus(record)}
              style={{ cursor: "pointer" }}
            >
              {record.is_active === 1 ? "Hoạt động" : "Không hoạt động"}
            </Tag>
          </Tooltip>
        ),
      },
    },
    actionColumnDef(
      (record) => [
        { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => navigate(`/update-menu/${record.id}`) },
        { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => showDeleteConfirm(record) },
      ],
      { width: 100 }
    ),
  ];
  //#endregion table

  //#region useEffect
  useEffect(() => {
    fetchActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion useEffect

  const columns = selectVisibleColumns(columnDefs, visibleColumns);

  return (
    <BrandedPageLayout
      title="Menu"
      subtitle="Quản lý và sắp xếp thứ tự menu điều hướng."
      breadcrumbItems={[{ title: "Menu" }]}
    >
      <ListToolbar
        filterDefs={[]}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        onReload={fetchActions}
        extra={
          <>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              style={{ backgroundColor: "#4096ff" }}
              onClick={handleSaveLocation}
              disabled={!hasChanges}
              loading={isLoading}
            >
              Lưu thứ tự Menu
            </Button>
            <NavLink to="/create-menu">
              <Button type="primary" icon={<PlusOutlined />}>
                Thêm menu
              </Button>
            </NavLink>
          </>
        }
      />

      <style>{`
        .custom-table .ant-table-body,
        .custom-table .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
          scrollbar-gutter: stable;
        }
      `}</style>

      <DndContext
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={visibleList.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <ListTableCard
            className="custom-table"
            columns={columns}
            dataSource={visibleList}
            loading={isLoading}
            components={{ body: { row: Row } }}
            pagination={false}
            expandIconColumnIndex={-1}
            scroll={{ y: 55 * 10, x: 'max-content' }}
            emptyText="Không có dữ liệu"
          />
        </SortableContext>
      </DndContext>
    </BrandedPageLayout>
  );
};

export default ListMenu;
